import type {
  LeaveApprovalItem,
  LeaveApprovalStatus,
  LeaveType,
} from "@/services/APIs/PtsLeaveApprovalService";

export const LEAVE_TYPE_OPTIONS = [
  { value: "full-day", label: "Full Day" },
  { value: "half-day", label: "Half Day" },
  { value: "wfh", label: "Work From Home" },
] as const;

export const LEAVE_APPROVAL_STATUS_OPTIONS = [
  "pending",
  "approved",
  "reject",
] as const;

export type NormalizedLeaveStatus = "pending" | "approved" | "reject" | "other";

export function normalizeLeaveStatus(
  status?: LeaveApprovalStatus | null
): NormalizedLeaveStatus {
  const value = String(status ?? "")
    .trim()
    .toLowerCase();

  if (value === "approved" || value === "approval") {
    return "approved";
  }

  if (value === "reject" || value === "rejected") {
    return "reject";
  }

  if (value === "pending" || value.includes("pending")) {
    return "pending";
  }

  return "other";
}

export function leaveStatusLabel(status?: LeaveApprovalStatus | null): string {
  const normalized = normalizeLeaveStatus(status);
  switch (normalized) {
    case "pending":
      return "Pending";
    case "approved":
      return "Approved";
    case "reject":
      return "Reject";
    default:
      return status ? String(status) : "—";
  }
}

export function leaveTypeLabel(leaveType?: LeaveType | null): string {
  const value = String(leaveType ?? "")
    .trim()
    .toLowerCase();
  if (value === "full-day" || value === "full day") {
    return "Full Day";
  }
  if (value === "half-day" || value === "half day") {
    return "Half Day";
  }
  if (value === "wfh") {
    return "Work From Home";
  }
  return leaveType ? String(leaveType) : "—";
}

export function leaveStatusBadgeClass(
  status?: LeaveApprovalStatus | null
): string {
  const normalized = normalizeLeaveStatus(status);

  switch (normalized) {
    case "pending":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "approved":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "reject":
      return "bg-rose-50 text-rose-700 border-rose-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
}

export function formatDateYmd(value?: string | null): string {
  if (!value) {
    return "—";
  }
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }
  const yyyy = parsed.getFullYear();
  const mm = String(parsed.getMonth() + 1).padStart(2, "0");
  const dd = String(parsed.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function formatDateDmy(value?: string | null): string {
  if (!value) {
    return "—";
  }
  const ymd = formatDateYmd(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    return ymd;
  }
  const [yyyy, mm, dd] = ymd.split("-");
  return `${dd}-${mm}-${yyyy}`;
}

export function resolveName(
  idOrName: string | undefined | null,
  lookup: Map<string, string>
): string {
  if (!idOrName) {
    return "—";
  }
  const key = String(idOrName);
  return lookup.get(key) ?? key;
}

export function matchesLeaveStatusFilter(
  itemStatus: string | null | undefined,
  filterStatus: string | undefined
): boolean {
  if (!filterStatus) {
    return true;
  }
  return (
    normalizeLeaveStatus(itemStatus) === normalizeLeaveStatus(filterStatus)
  );
}

export function filterLeaveApprovals(
  items: LeaveApprovalItem[],
  filters: {
    leaveType?: string;
    status?: string;
    search?: string;
  }
): LeaveApprovalItem[] {
  const search = String(filters.search ?? "")
    .trim()
    .toLowerCase();

  return items.filter(item => {
    if (
      filters.leaveType &&
      String(item.leave_type).toLowerCase() !==
        String(filters.leaveType).toLowerCase()
    ) {
      return false;
    }
    if (!matchesLeaveStatusFilter(item.status, filters.status)) {
      return false;
    }
    if (search) {
      const haystack = [
        item.requested_by_name,
        item.requested_by,
        item.reason,
        item.leave_type,
        item.status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(search)) {
        return false;
      }
    }
    return true;
  });
}
